/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { BridgeCommands } from 'bridge-commands/bridge.commands';
import { BridgeServiceInterface } from './bridge-service.interface';
import { BridgeConfig, BridgeMock } from './bridge.config';

function generateBridgeMockKey<ParamType extends object, ResponseType>(
    command: BridgeCommands.Command<ParamType, ResponseType>
): string {
    return [command.system, command.module, command.method, command.legacyCommand].join('.');
}

export class BridgeMockCallData {
    count: number;
    params: object[];
}

@Injectable({
    providedIn: 'root'
})
export class BridgeMockService implements BridgeServiceInterface {
    private responses: { [key: string]: any } = {};
    private persistentResponses: { [key: string]: Observable<any> } = {};
    private errors: { [key: string]: any } = {};
    private configMocks: { [key: string]: object } = {};
    private callData: {
        [k: string]: BridgeMockCallData;
    } = {};

    constructor(private bridgeConfig: BridgeConfig) {
        for (const mockObj of Object.values(bridgeConfig.mocks)) {
            this.configMocks[generateBridgeMockKey(mockObj.command)] = mockObj.return;
        }
    }

    public createResponse<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        response: ResponseType
    ): void {
        const key: string = generateBridgeMockKey(command);
        delete this.errors[key];
        this.responses[key] = response;
    }

    public createResponseSequence<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        response: ResponseType[]
    ): void {
        const key: string = generateBridgeMockKey(command);
        delete this.errors[key];
        this.responses[key] = response;
    }

    public createPersistentResponse<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        responseSource: Observable<ResponseType>
    ): void {
        if (command.persistent) {
            const key: string = generateBridgeMockKey(command);
            delete this.errors[key];
            this.persistentResponses[key] = responseSource;
        } else {
            throw new Error('Can only create a persistent response for persistent commands');
        }
    }

    public createErrorResponse<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        error: any
    ): void {
        const key: string = generateBridgeMockKey(command);
        delete this.responses[key];
        this.errors[key] = error;
    }

    public executeCommand<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params?: ParamType
    ): Observable<ResponseType> {
        const key: string = generateBridgeMockKey(command);
        this.cacheCallData(key, params);

        let response = of(undefined);
        // Top priority are manually set responses (overrides in unit tests, or other services)
        if (key in this.errors) {
            response = throwError(this.errors[key]);
        } else if (key in this.responses) {
            // If this is a sequence of responses, keep popping off the response
            // we've used
            if (Array.isArray(this.responses[key])) {
                response = of(this.responses[key].shift());
            } else {
                response = of(this.responses[key]);
            }
        } else if (key in this.configMocks) {
            response = of(this.configMocks[key]);
        } else if (key in this.persistentResponses) {
            response = this.persistentResponses[key];
        }

        return response;
    }

    private hasOverrideMock<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>
    ): boolean {
        const key = generateBridgeMockKey(command);
        return key in this.responses || key in this.persistentResponses;
    }

    private hasErrorMock<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>
    ): boolean {
        return generateBridgeMockKey(command) in this.errors;
    }

    private hasConfigMock<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>
    ): boolean {
        return generateBridgeMockKey(command) in this.configMocks;
    }

    public hasMock<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>
    ): boolean {
        const hasMock: boolean =
            this.hasOverrideMock(command) || this.hasConfigMock(command) || this.hasErrorMock(command);

        // This is a special test for unit tests to make it more obvious when
        // a test has not been mocked
        if ((this.bridgeConfig as any).isUnitTestConfig && !hasMock) {
            console.error('No mock provided for: ' + generateBridgeMockKey(command));
        }

        return hasMock;
    }

    private cacheCallData<ParamType extends object>(key: string, params?: ParamType): void {
        if (this.bridgeConfig.cacheMockedCallData) {
            if (!(key in this.callData)) {
                this.callData[key] = {
                    count: 0,
                    params: []
                };
            }
            this.callData[key].count++;
            this.callData[key].params.push(params);
        }
    }

    public resetCallData(): void {
        this.callData = {};
    }

    public getCallData<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>
    ): BridgeMockCallData {
        const key: string = generateBridgeMockKey(command);
        if (key in this.callData) {
            return this.callData[key];
        } else {
            return {
                count: 0,
                params: []
            };
        }
    }
}
